import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { getCategoryName } from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章不存在</h1>
        <p className="text-foreground-500 mb-6">该文章可能已被删除或链接有误</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors text-sm font-medium whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 4);
  const categoryName = getCategoryName(article.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '吃瓜爆料',
      url: 'https://www.chiguabaoliao.com',
    },
    url: `https://www.chiguabaoliao.com/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.chiguabaoliao.com/article/${article.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.chiguabaoliao.com' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.chiguabaoliao.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 吃瓜爆料`}
        description={article.excerpt.slice(0, 150)}
        keywords={`吃瓜爆料,${article.tags.join(',')},${categoryName}`}
        canonicalUrl={`https://www.chiguabaoliao.com/article/${article.id}`}
        ogType="article"
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <article className="max-w-[1280px] mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <header className="mb-8">
              <div className="flex items-center flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center flex-wrap gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-xs"></i>
                  </div>
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs"></i>
                  </div>
                  <span>{article.publishDate}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </div>
                  <span>{(article.readCount / 10000).toFixed(1)}万阅读</span>
                </div>
              </div>
            </header>

            <div className="relative rounded-2xl overflow-hidden aspect-[16/8] mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center justify-between">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </div>
                  返回{categoryName}
                </Link>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-400">分享到：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="p-5 rounded-xl bg-accent-50/50 border border-accent-200/50">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-links-line text-accent-500"></i>
                  </div>
                  相关推荐
                </h3>
                <div className="space-y-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="block group cursor-pointer"
                    >
                      <div className="relative rounded-lg overflow-hidden aspect-[16/9] mb-2">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                        {rel.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-1 block">{rel.publishDate}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}